import React from "react";
import { Stack } from "@fluentui/react";
import { DismissRegular } from "@fluentui/react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import styles from "./CitationPanel.module.css";
import { Citation } from "../../api/models";

type CitationPanelProps = {
  activeCitation: Citation;
  setIsCitationPanelOpen: (flag: boolean) => void;
};

function getTitle(citation: Citation) {
  if (citation.url && citation.url.trim() !== "") {
    return <a href={`${citation.url}`} target="_blank" rel="noopener noreferrer">
        <h5 className={`${styles.citationPanelTitle} ${styles.mobileCitationPanelTitle}`}>
          {citation.title}
        </h5>
      </a>;
  }

  return <h5 className={`${styles.citationPanelTitle} ${styles.mobileCitationPanelTitle}`}>
          {citation.title}
        </h5>
}

export const CitationPanel: React.FC<CitationPanelProps> = (props) => {
  const { activeCitation, setIsCitationPanelOpen } = props;

  // Custom link component for ReactMarkdown to open links in new tab
  const LinkRenderer = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  };

  return (
    <Stack.Item className={`${styles.citationPanel} ${styles.mobileStyles}`}>
      <Stack
        horizontal
        className={styles.citationPanelHeaderContainer}
        horizontalAlign="space-between"
        verticalAlign="center"
      >
        <span className={styles.citationPanelHeader}>Citations</span>
        <DismissRegular
          role="button"
          onKeyDown={(e) =>
            e.key === " " || e.key === "Enter"
              ? setIsCitationPanelOpen(false)
              : null
          }
          tabIndex={0}
          className={styles.citationPanelDismiss}
          onClick={() => setIsCitationPanelOpen(false)}
        />
      </Stack>     
      {getTitle(activeCitation)}      
      <ReactMarkdown
        className={`${styles.citationPanelContent} ${styles.mobileCitationPanelContent}`}
        children={activeCitation.content}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: LinkRenderer
        }}
      />
      <div
        className={`${styles.citationPanelDisclaimer} ${styles.mobileCitationPanelDisclaimer}`}
      >
        Tables, images, and other special formatting not shown in this preview.
        Please follow the link to review the original document.
      </div>
    </Stack.Item>
  );
};
